import { formatCurrency, PROPERTY_TYPES, type RealEstateSaleWorksheetInput, type RealEstateSaleWorksheetResult } from "./realEstateSaleWorksheet";

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    "\"": "&quot;",
  })[character] ?? character);
}

function displayDate(value: string): string {
  if (!value) return "—";
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function resultRow(label: string, value: number, emphasis = false): string {
  return `<tr class="${emphasis ? "emphasis" : ""}"><td>${escapeHtml(label)}</td><td>${formatCurrency(value)}</td></tr>`;
}

function inputRow(label: string, value: string): string {
  return `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`;
}

export function buildDepreciationReportHtml(input: RealEstateSaleWorksheetInput, result: RealEstateSaleWorksheetResult): string {
  const now = new Date();
  const property = PROPERTY_TYPES.find((option) => option.value === input.propertyType) ?? PROPERTY_TYPES[0]!;
  const generatedAt = now.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  const assetDetails = [
    inputRow("Type of asset", property.label),
    inputRow("Acquisition Date", displayDate(input.acquisitionDate)),
    inputRow("Sale Date", displayDate(input.saleDate)),
  ].join("");
  const saleInputs = [
    inputRow("Purchase Price", formatCurrency(input.purchasePrice)),
    inputRow("Acquisition Costs", formatCurrency(input.acquisitionCosts)),
    inputRow("Improvements", formatCurrency(input.improvements)),
    inputRow("Selling Expenses", formatCurrency(input.sellingExpenses)),
    inputRow("Selling Price", formatCurrency(input.sellingPrice)),
  ].join("");
  const saleResults = [
    resultRow("Net sale proceeds", result.netProceeds),
    resultRow("Cost basis before depreciation", result.costBasisBeforeDepreciation),
    resultRow("Net profit/loss before depreciation", result.gainOrLossBeforeDepreciation, true),
  ].join("");
  const depreciationInputs = [
    inputRow("Land Value", formatCurrency(input.landValue)),
    inputRow("Purchase value less land value", formatCurrency(result.depreciableBuildingValue)),
    inputRow("Recovery life", result.recoveryYears ? `${result.recoveryYears} years` : input.propertyType === "manual" ? "Manual annual depreciation" : "No depreciation"),
    inputRow("Holding period", input.acquisitionDate && input.saleDate ? `${result.yearsHeld} years, ${result.remainingMonths} months` : "—"),
  ].join("");
  const depreciationResults = [
    resultRow("Annual depreciation", result.annualDepreciation),
    resultRow("Calculated depreciation", result.calculatedDepreciation),
    resultRow("Depreciation allowed/allowable used", result.depreciationAllowedOrAllowable, true),
  ].join("");
  const finalResults = [
    resultRow("Adjusted basis after depreciation", result.adjustedBasis),
    resultRow("Total gain/loss after depreciation", result.totalGainOrLoss, true),
    resultRow("Depreciation-related gain / recapture review", result.depreciationRelatedGainReview, true),
    resultRow("Remaining capital / §1231 gain or loss", result.remainingGainOrLoss, true),
  ].join("");

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>APX Depreciation Report Worksheet</title>
<style>
@page { size: letter portrait; margin: 0.42in; }
* { box-sizing: border-box; }
body { margin: 0; color: #14223a; background: #fff; font-family: Arial, Helvetica, sans-serif; }
.report { max-width: 7.7in; margin: 0 auto; }
.top { display: flex; justify-content: space-between; gap: 24px; border-bottom: 3px solid #b68125; padding-bottom: 12px; }
.eyebrow { margin: 0 0 4px; color: #8a651e; font-size: 10px; font-weight: 700; letter-spacing: .11em; text-transform: uppercase; }
h1 { margin: 0; font-family: Georgia, "Times New Roman", serif; font-size: 27px; line-height: 1.1; }
.meta { margin: 4px 0 0; color: #556274; font-size: 11px; }
.generated { color: #556274; font-size: 10px; text-align: right; white-space: nowrap; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 16px; }
.card { border: 1px solid #c9d2dc; border-radius: 7px; overflow: hidden; break-inside: avoid; }
.card.full { grid-column: 1 / -1; }
.card-title { margin: 0; padding: 8px 11px; background: #14223a; color: #fff; font-family: Georgia, "Times New Roman", serif; font-size: 15px; }
.card-subtitle { margin: 0; padding: 0 11px 8px; background: #14223a; color: #d7e0ea; font-size: 10px; }
table { width: 100%; border-collapse: collapse; font-size: 11px; }
td { padding: 7px 10px; border-top: 1px solid #e1e6eb; vertical-align: top; }
td:first-child { color: #46566a; width: 63%; }
td:last-child { color: #14223a; font-weight: 700; text-align: right; font-variant-numeric: tabular-nums; }
tr.emphasis td { background: #fff8e7; border-top: 1px solid #d9b66a; color: #33240c; font-size: 12px; font-weight: 800; }
.final tr.emphasis td { background: #f6efd9; }
.note { margin-top: 14px; border-top: 1px solid #d7dee6; padding-top: 9px; color: #5b6878; font-size: 9.5px; line-height: 1.45; }
.footer { display: flex; justify-content: space-between; margin-top: 11px; color: #7b8795; font-size: 9px; }
.print-btn { display: block; margin: 0 auto 20px; border: 0; border-radius: 6px; background: #14223a; color: #fff; cursor: pointer; padding: 10px 28px; font-size: 13px; font-weight: 700; }
@media screen { body { padding: 28px; background: #f3f5f7; } .report { background: #fff; padding: 28px; box-shadow: 0 4px 22px rgba(20,34,58,.15); } }
@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .print-btn { display: none !important; } }
</style></head>
<body><button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
<article class="report">
  <header class="top"><div><p class="eyebrow">APX WorkCenter · Tax & Controller Workpaper</p><h1>Depreciation Report Worksheet</h1><p class="meta">Real estate sale profit/loss and separate depreciation review</p></div><p class="generated">Generated<br>${escapeHtml(generatedAt)}</p></header>
  <div class="grid">
    <section class="card"><h2 class="card-title">Property Details</h2><table>${assetDetails}</table></section>
    <section class="card"><h2 class="card-title">Depreciation Inputs</h2><table>${depreciationInputs}</table></section>
    <section class="card"><h2 class="card-title">Sale Inputs</h2><table>${saleInputs}</table></section>
    <section class="card"><h2 class="card-title">Sale Result Before Depreciation</h2><table>${saleResults}</table></section>
    <section class="card"><h2 class="card-title">Depreciation Calculation</h2><table>${depreciationResults}</table></section>
    <section class="card final"><h2 class="card-title">Gain / Depreciation Separation</h2><table>${finalResults}</table></section>
  </div>
  <p class="note">Internal planning worksheet. The report uses a direct annual estimate for the selected asset type. Confirm final depreciation schedules, land/building allocation, and return reporting treatment in the applicable tax workpapers.</p>
  <footer class="footer"><span>apxsystem.io</span><span>Depreciation Report Worksheet</span></footer>
</article></body></html>`;
}

export function openDepreciationReportPrint(input: RealEstateSaleWorksheetInput, result: RealEstateSaleWorksheetResult): void {
  const printWindow = window.open("", "_blank", "width=1000,height=900,scrollbars=yes,resizable=yes");
  if (!printWindow) {
    window.alert("Please allow pop-ups for this site to print or save the report as PDF.");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(buildDepreciationReportHtml(input, result));
  printWindow.document.close();
}
